import React, { useState, useCallback, useRef, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption, SyutGrid, type SyutGridColumn } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { reportService, coaLedgerService } from 'api/services';
import type { LedgerStatementDto, LedgerStatementLineDto } from 'api/types';
import { useHasPermission } from 'hooks/usePermissions';
import ReportHeader from 'components/reports/ReportHeader';
import DrillableCell from 'components/reports/DrillableCell';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '');
const fmtBal = (n: number) => `${money(Math.abs(n))} ${n >= 0 ? 'Dr' : 'Cr'}`;

const inp: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem' };

// SyutGrid requires each row to have an `id` — synthesize one from transactionId + index
// because a transaction can produce multiple movements on the same ledger.
interface LedgerLineRow extends LedgerStatementLineDto {
  id: string;
}

interface LedgerStatementScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

const LedgerStatementScreen: React.FC<LedgerStatementScreenProps> = ({ moduleIcon, tabId }) => {
  const canExport = useHasPermission('ledger-statement', 'EXPORT');
  const [filterOpen, setFilterOpen] = useState(false);
  const [ledgerId, setLedgerId] = useState('');
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState<LedgerStatementDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const labelCache = useRef<Map<string, string>>(new Map());
  const ledgerLabel = ledgerId ? (labelCache.current.get(ledgerId) || '') : '';

  // Give each movement a unique row id for SyutGrid.
  const gridRows: LedgerLineRow[] = useMemo(
    () => (data?.lines ?? []).map((l, i) => ({ ...l, id: `${l.transactionId}-${i}` })),
    [data],
  );

  const loadLedgers = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(l => labelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`));
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  const run = useCallback(async () => {
    if (!ledgerId) { notificationService.toast.warning('Select a ledger.'); return; }
    setLoading(true);
    try {
      setData(await reportService.getLedger(ledgerId, fromDate, toDate));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the ledger statement.');
    } finally {
      setLoading(false);
    }
  }, [ledgerId, fromDate, toDate]);

  const clear = useCallback(
    () => { setLedgerId(''); setFromDate(monthStart()); setToDate(today()); setData(null); },
    [],
  );

  const handleExport = useCallback(async () => {
    if (!ledgerId) { notificationService.toast.warning('Select a ledger to export.'); return; }
    setExporting(true);
    try {
      await reportService.exportLedger(ledgerId, fromDate, toDate);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not export the ledger statement.');
    } finally {
      setExporting(false);
    }
  }, [ledgerId, fromDate, toDate]);

  // Export button appears only when the user has UI.LEDGER-STATEMENT.EXPORT permission.
  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(run),
    ...(canExport ? [{ ...actionButtonFactory.export_(handleExport), disabled: exporting }] : []),
  ];

  // ------------------------------------------------------------------
  // SyutGrid columns. Drill-down TODO: wrap Voucher No in DrillableCell
  // with onClick to open the source voucher (JE / SI / etc.).
  // ------------------------------------------------------------------
  const columns: SyutGridColumn<LedgerLineRow>[] = useMemo(() => [
    {
      id: 'date',
      header: 'Date',
      size: 100,
      cell: ({ row }) => fmtDate(row.original.transactionDate),
    },
    {
      id: 'voucherNo',
      header: 'Voucher',
      size: 130,
      cell: ({ row }) => <DrillableCell>{row.original.transactionNo}</DrillableCell>,
    },
    {
      id: 'voucherType',
      header: 'Type',
      size: 130,
      cell: ({ row }) => row.original.voucherTypeName || '—',
    },
    {
      id: 'narration',
      header: 'Particulars',
      cell: ({ row }) => row.original.narration || '—',
    },
    {
      id: 'debit',
      header: 'Debit',
      align: 'right',
      size: 110,
      cell: ({ row }) => row.original.drAmount ? money(row.original.drAmount) : '',
    },
    {
      id: 'credit',
      header: 'Credit',
      align: 'right',
      size: 110,
      cell: ({ row }) => row.original.crAmount ? money(row.original.crAmount) : '',
    },
    {
      id: 'balance',
      header: 'Balance',
      align: 'right',
      size: 140,
      cell: ({ row }) => fmtBal(row.original.runningBalance),
    },
  ], []);

  return (
    <EnterpriseScreenShell
      title="Ledger"
      description="Opening balance, movements and closing balance for a ledger"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Ledger" isAsync loadOptions={loadLedgers}
                  defaultOptions cacheOptions={false}
                  seedOptions={ledgerId
                    ? [{ value: ledgerId, label: labelCache.current.get(ledgerId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={ledgerId}
                  onChange={v => setLedgerId((v as string) || '')}
                  placeholder="Pick a ledger" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)} style={inp} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)} style={inp} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clear}>Clear</button>
              <button className="btn btn-primary" onClick={run} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader
        filters={[
          { label: 'Ledger', value: ledgerLabel },
          { label: 'Period', value: `${fmtDate(fromDate)} — ${fmtDate(toDate)}` },
        ]}
      />
      {!data && (
        <div style={{
          color: 'var(--text-muted)', padding: '2rem', textAlign: 'center',
          border: '1px solid var(--border-color)', borderTop: 'none', borderRadius: '0 0 0.5rem 0.5rem'
        }}>
          Pick a ledger and click Apply.
        </div>
      )}
      {data && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
          border: '1px solid var(--border-color)', borderTop: 'none',
          borderRadius: '0 0 0.5rem 0.5rem', overflow: 'hidden',
        }}>
          {/* Opening balance band — sits ABOVE the movements grid. */}
          <div style={{
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border-color)',
            fontSize: '0.8125rem',
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>Opening Balance</span>
            <span>{fmtBal(data.openingBalance)}</span>
          </div>
          {/* Movements — SyutGrid. */}
          {/* Flex column so SyutGrid (flex: 1) gets a bounded height and scrolls; a block parent
              with overflow: hidden clipped the later movements and the pager. */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <SyutGrid<LedgerLineRow>
              data={gridRows}
              columns={columns}
              isLoading={loading}
              emptyMessage="No movements in this date range"
            />
          </div>
          {/* Period Total + Closing Balance — sit BELOW the movements grid. */}
          <div style={{
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-secondary)',
            borderTop: '1px solid var(--border-color)',
            fontSize: '0.8125rem',
            fontWeight: 700,
            display: 'grid',
            gridTemplateColumns: '1fr auto auto auto',
            gap: '1.5rem',
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>Period Total</span>
            <span>Dr {money(data.totalDr)}</span>
            <span>Cr {money(data.totalCr)}</span>
            <span />
          </div>
          <div style={{
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-secondary)',
            borderTop: '1px solid var(--border-color)',
            fontSize: '0.8125rem',
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>Closing Balance</span>
            <span>{fmtBal(data.closingBalance)}</span>
          </div>
        </div>
      )}
    </EnterpriseScreenShell>
  );
};

export default LedgerStatementScreen;
